import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { InvoiceState } from '@treadinc/horizon-api-spec';
import { every } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Trans } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import StatusTabs, { StatusTab } from '~components/Tabs/StatusTabs';
import { TabQSParam } from '~pages/Approvals/DriverPay/TabQSParam';

import DriverPayPageContext from '../DriverPayPageContext';
import DriverPayQueryContext from '../DriverPayQueryContext';

type InvoiceStateTab = StatusTab<TabQSParam> & { filters: InvoiceState[] };

const DriverPayTabs = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const driverPayQuery = useContext(DriverPayQueryContext);
  const { uncheckAllInvoices } = useContext(DriverPayPageContext);

  const hasAppliedInitialFilters = useRef(false);
  const previousTab = useRef<TabQSParam | null>(null);

  // Helper function to create Trans labels
  const createTransLabel = (i18nKey: string) => (
    <Trans
      i18nKey={i18nKey}
      components={{
        style: <Typography component="span" variant="body2" />,
      }}
    />
  );

  const driverPayStateTabs: InvoiceStateTab[] = useMemo(() => {
    return [
      {
        label: createTransLabel('approvals.driver_pay.tabs.invoices'),
        value: TabQSParam.INVOICES,
        filters: [
          InvoiceState.PENDING,
          InvoiceState.CUSTOMER_PENDING,
          InvoiceState.APPROVED,
          InvoiceState.CUSTOMER_APPROVED,
        ],
      },
      {
        label: createTransLabel('approvals.driver_pay.tabs.settlements'),
        value: TabQSParam.SETTLEMENTS,
        filters: [],
      },
    ];
  }, []);

  const updateSearchParams = useCallback(
    (newParams: Array<{ key: string; value: string }>) => {
      setSearchParams((currentParams) => {
        newParams.forEach((param) => {
          currentParams.set(param.key, param.value);
        });

        return currentParams;
      });
    },
    [setSearchParams],
  );

  const areFiltersEqual = useCallback(
    (tabFilters: InvoiceState[]) => {
      const currentInvoiceStates = driverPayQuery.filters.invoiceStates || [];

      // If both are empty, consider them equal
      if (!tabFilters.length && !currentInvoiceStates.length) {
        return true;
      }

      // Compare arrays - order doesn't matter for filters
      return (
        tabFilters.length === currentInvoiceStates.length &&
        every(tabFilters, (state) => currentInvoiceStates.includes(state))
      );
    },
    [driverPayQuery.filters.invoiceStates],
  );

  const handleStateTabChange = useCallback(
    (value: TabQSParam) => {
      uncheckAllInvoices();
      updateSearchParams([{ key: 'tab', value: value as string }]);

      // Find the selected tab to get its filters
      const selectedTab = driverPayStateTabs.find((tab) => tab.value === value);
      if (selectedTab) {
        const tabFilters = selectedTab.filters;

        // Only apply filters if they're different from current filters
        if (!areFiltersEqual(tabFilters)) {
          driverPayQuery.applyFilters({
            invoiceStates: tabFilters.length > 0 ? tabFilters : undefined,
            grouped: undefined,
          });
        }
      }
    },
    [updateSearchParams, driverPayStateTabs, driverPayQuery, areFiltersEqual],
  );

  // Apply initial filters when component mounts or tab changes
  useEffect(() => {
    const currentTab = (searchParams.get('tab') as TabQSParam) ?? TabQSParam.INVOICES;
    const selectedTab = driverPayStateTabs.find((tab) => tab.value === currentTab);

    // Only apply filters on initial mount or when tab actually changes
    const isTabChange =
      previousTab.current !== null && previousTab.current !== currentTab;
    const shouldApplyFilters = !hasAppliedInitialFilters.current || isTabChange;

    if (selectedTab && shouldApplyFilters) {
      if ((searchParams.get('tab') as TabQSParam) !== currentTab) {
        updateSearchParams([{ key: 'tab', value: currentTab as string }]);
      }

      const tabFilters = selectedTab.filters;

      // Only apply filters if they're different from current filters
      if (!areFiltersEqual(tabFilters)) {
        driverPayQuery.applyFilters({
          invoiceStates: tabFilters.length > 0 ? tabFilters : undefined,
          grouped: undefined,
        });
      }

      hasAppliedInitialFilters.current = true;
    }

    previousTab.current = currentTab;
  }, [
    driverPayStateTabs,
    driverPayQuery,
    searchParams,
    areFiltersEqual,
    updateSearchParams,
  ]);

  return (
    <Box>
      <StatusTabs
        tabs={driverPayStateTabs}
        selectedTab={(searchParams.get('tab') as TabQSParam) ?? TabQSParam.INVOICES}
        onChange={handleStateTabChange}
      />
    </Box>
  );
};

export default DriverPayTabs;
